import { initializeConnector } from "@web3-react/core";
import { Connector, Actions } from "@web3-react/types";

function parseChainId(chainId: string | number): number {
  return typeof chainId === "string"
    ? Number.parseInt(chainId, 16)
    : chainId;
}

/**
 * A generic EIP-1193 connector that wraps any injected provider.
 * Used for EIP-6963 discovered wallets (Rabby, Trust, OKX, Phantom, etc.)
 */
class EIP1193Connector extends Connector {
  private eip1193Provider: any | undefined;

  constructor({ actions, onError }: { actions: Actions; onError?: (error: Error) => void }) {
    super(actions, onError);
  }

  /** Set the provider from EIP-6963 discovery before activating */
  setProvider(provider: any) {
    // Clean up old listeners
    if (this.eip1193Provider && this.eip1193Provider !== provider) {
      this.eip1193Provider.removeListener?.("chainChanged", this.onChainChanged);
      this.eip1193Provider.removeListener?.("accountsChanged", this.onAccountsChanged);
      this.eip1193Provider.removeListener?.("disconnect", this.onDisconnect);
    }
    this.eip1193Provider = provider;
    this.provider = provider;
  }

  private onChainChanged = (chainId: string | number) => {
    this.actions.update({ chainId: parseChainId(chainId) });
  };

  private onAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      this.actions.resetState();
    } else {
      this.actions.update({ accounts });
    }
  };

  private onDisconnect = () => {
    this.actions.resetState();
  };

  async connectEagerly(): Promise<void> {
    if (!this.eip1193Provider) return;

    try {
      const accounts: string[] = await this.eip1193Provider.request({
        method: "eth_accounts",
      });
      if (!accounts.length) throw new Error("No accounts");

      const chainId: string = await this.eip1193Provider.request({
        method: "eth_chainId",
      });

      this.actions.update({ chainId: parseChainId(chainId), accounts });
      this.setupListeners();
    } catch {
      // Silently fail eager connection
    }
  }

  async activate(desiredChainIdOrParams?: number | any): Promise<void> {
    if (!this.eip1193Provider) {
      throw new Error("No provider set. Call setProvider() first.");
    }

    const accounts: string[] = await this.eip1193Provider.request({
      method: "eth_requestAccounts",
    });

    const chainId: string = await this.eip1193Provider.request({
      method: "eth_chainId",
    });

    const currentChainId = parseChainId(chainId);

    // Extract the desired chain ID from params (can be a number or AddEthereumChainParameter object)
    let desiredChainId: number | undefined;
    let addChainParams: any | undefined;

    if (typeof desiredChainIdOrParams === "number") {
      desiredChainId = desiredChainIdOrParams;
    } else if (desiredChainIdOrParams && typeof desiredChainIdOrParams === "object" && desiredChainIdOrParams.chainId) {
      desiredChainId = desiredChainIdOrParams.chainId;
      addChainParams = desiredChainIdOrParams;
    }

    // If a specific chain is desired and we're on the wrong one, try to switch
    if (desiredChainId && currentChainId !== desiredChainId) {
      try {
        await this.eip1193Provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: `0x${desiredChainId.toString(16)}` }],
        });
      } catch (switchError: any) {
        // If the chain hasn't been added (4902), try adding it
        if (switchError.code === 4902 && addChainParams) {
          await this.eip1193Provider.request({
            method: "wallet_addEthereumChain",
            params: [{ ...addChainParams, chainId: `0x${desiredChainId.toString(16)}` }],
          });
        } else {
          throw switchError;
        }
      }
      const newChainId: string = await this.eip1193Provider.request({
        method: "eth_chainId",
      });
      this.actions.update({ chainId: parseChainId(newChainId), accounts });
    } else {
      this.actions.update({ chainId: currentChainId, accounts });
    }

    this.setupListeners();
  }

  private setupListeners() {
    if (!this.eip1193Provider) return;
    // Idempotent: remove before re-adding so calling this twice (e.g. an SDK that
    // re-issues the same provider object across renders) can't leak duplicate
    // listeners. Handlers are stable class fields, so remove-by-reference works.
    this.eip1193Provider.removeListener?.("chainChanged", this.onChainChanged);
    this.eip1193Provider.removeListener?.("accountsChanged", this.onAccountsChanged);
    this.eip1193Provider.removeListener?.("disconnect", this.onDisconnect);
    this.eip1193Provider.on?.("chainChanged", this.onChainChanged);
    this.eip1193Provider.on?.("accountsChanged", this.onAccountsChanged);
    this.eip1193Provider.on?.("disconnect", this.onDisconnect);
  }

  /**
   * Adopt an already-connected EIP-1193 provider coming from an external wallet
   * SDK (Privy / Para / DogeOS). Unlike `activate()`, this does NOT call
   * `eth_requestAccounts` — the SDK's own modal already authorized the account, so
   * re-requesting would risk a duplicate prompt. We just wire the provider into
   * web3-react state and (re)attach change listeners.
   */
  async adopt(
    provider: any,
    state: { accounts: string[]; chainId: number }
  ): Promise<void> {
    this.setProvider(provider);
    this.actions.update({ chainId: state.chainId, accounts: state.accounts });
    this.setupListeners();
  }

  /** Switch chain only — does NOT re-request accounts. */
  async switchChain(chainId: number, addChainParams?: any): Promise<void> {
    if (!this.eip1193Provider) {
      throw new Error("No provider set.");
    }
    const hexChainId = `0x${chainId.toString(16)}`;
    try {
      await this.eip1193Provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hexChainId }],
      });
    } catch (switchError: any) {
      // 4902 = chain not added; some wallets (e.g. older Rabby) use -32603
      const notAdded = switchError.code === 4902 || switchError.code === -32603;
      if (notAdded && addChainParams) {
        await this.eip1193Provider.request({
          method: "wallet_addEthereumChain",
          params: [{ ...addChainParams, chainId: hexChainId }],
        });
      } else {
        throw switchError;
      }
    }
    // Explicitly update web3-react state — don't rely solely on chainChanged event
    const newChainId: string = await this.eip1193Provider.request({
      method: "eth_chainId",
    });
    this.actions.update({ chainId: parseChainId(newChainId) });
  }

  async deactivate(): Promise<void> {
    if (this.eip1193Provider) {
      this.eip1193Provider.removeListener?.("chainChanged", this.onChainChanged);
      this.eip1193Provider.removeListener?.("accountsChanged", this.onAccountsChanged);
      this.eip1193Provider.removeListener?.("disconnect", this.onDisconnect);
    }
    // Drop the provider too, not just its listeners. getActiveEip1193() reads this
    // field directly, so leaving it set after a disconnect means the next signing
    // attempt silently uses the wallet the user just walked away from.
    this.eip1193Provider = undefined;
    this.provider = undefined;
    this.actions.resetState();
  }
}

export const [eip6963Connector, hooks] = initializeConnector<EIP1193Connector>(
  (actions) => new EIP1193Connector({ actions })
);

export { EIP1193Connector };
